import type { CalculateStripePricingBreakdownRequest } from '@aloft/toolkit-shared';
import { Flex, Typography } from '@aloft/toolkit-sui';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useState } from 'react';

import { getConfig } from '../../../../utils/getConfig.ts';
import { useCalculatePricingBreakdown } from '../../hooks/useCalculatePricingBreakdown.ts';
import { getPlan } from '../../utils/getPlan.ts';
import { BillingAddressForm } from './BillingAddressForm.tsx';
import { BillingAddressPreview } from './BillingAddressPreview.tsx';
import { CheckoutForm } from './CheckoutForm.tsx';
import { CheckoutStep } from './CheckoutStep.tsx';
import { CheckoutSteps } from './CheckoutSteps.tsx';
import { PricingBreakdownSummary } from './PricingBreakdownSummary.tsx';
import { ProductInfo } from './ProductInfo.tsx';
import { TrialInfo } from './TrialInfo.tsx';
import { elementsAppearance } from './elementsAppearance.ts';
import type { CheckoutProps, PricingBreakdown, StripeAddressElementValue } from './types.ts';

const stripePromise = loadStripe(getConfig('STRIPE_PT_PUBLISHABLE_KEY'));

const addressStateToRequest = (
  address?: StripeAddressElementValue,
): CalculateStripePricingBreakdownRequest['address'] => {
  if (!address?.address) return undefined;

  return {
    ...address.address,
    line2: address.address.line2 ?? undefined,
  };
};

export const Checkout = (props: CheckoutProps) => {
  const { priceID, redirectURL, initialPricingBreakdown, mode, onCheckoutComplete } = props;
  const { price } = getPlan(priceID);

  const [address, setAddress] = useState<StripeAddressElementValue>();
  const [pricingBreakdown, setPricingBreakdown] =
    useState<PricingBreakdown>(initialPricingBreakdown);
  const [promoCode, setPromoCode] = useState<string | null>(null);

  const { calculatePricingBreakdown } = useCalculatePricingBreakdown();

  const recalculatePricingDetails = async (params: CalculateStripePricingBreakdownRequest) => {
    const res = await calculatePricingBreakdown(params);
    setPricingBreakdown(res);
  };

  const isTrial = mode === 'legacy-new-user-trial';

  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: 'subscription',
        amount: price.unitAmount,
        currency: 'usd',
        fonts: [{ cssSrc: 'https://fonts.googleapis.com/css?family=Lato' }],
        appearance: elementsAppearance,
      }}
    >
      <Flex width="100%" justifyContent="center" alignItems="center">
        <Flex flexDirection="column" gap="2rem" justifyContent="center" flexBasis="650px">
          <Flex flexDirection="column" gap="1.5rem">
            <Flex flexDirection="column">
              <Typography variant="h2">Subscribe to Appraiser Toolkit</Typography>
              {mode === 'add-payment-method' && (
                <Typography color="textSecondary">
                  Payment will be collected at the end of your trial period.
                </Typography>
              )}
              <ProductInfo priceID={priceID} isTrial={isTrial} />
            </Flex>
            <PricingBreakdownSummary
              pricingBreakdown={pricingBreakdown}
              onPromoCodeFormSuccess={(promoCode) => {
                setPromoCode(promoCode);
                recalculatePricingDetails({
                  priceID,
                  isTrial: !!isTrial,
                  address: addressStateToRequest(address),
                  promotionCode: promoCode,
                });
              }}
            />
          </Flex>
          <CheckoutSteps>
            <CheckoutStep step={0} title="Billing details">
              {({ isComplete }) => (
                <>
                  {isComplete && address && <BillingAddressPreview address={address} />}
                  {/* NOTE: This keeps the form in the DOM to preserve Stripe context */}
                  <div style={{ display: isComplete ? 'none' : undefined }}>
                    <BillingAddressForm
                      onComplete={async ({ address }) => {
                        setAddress(address);
                        await recalculatePricingDetails({
                          priceID,
                          isTrial: !!isTrial,
                          address: addressStateToRequest(address),
                          promotionCode: promoCode ?? undefined,
                        });
                      }}
                    />
                  </div>
                </>
              )}
            </CheckoutStep>
            <CheckoutStep step={1} title="Payment details">
              <CheckoutForm
                priceID={priceID}
                redirectURL={redirectURL}
                onCheckoutComplete={onCheckoutComplete}
                promoCode={promoCode}
                mode={mode}
              />
              {isTrial && <TrialInfo priceID={priceID} />}
            </CheckoutStep>
          </CheckoutSteps>
        </Flex>
      </Flex>
    </Elements>
  );
};
